import { Button, TabCard, TextField } from '@cimpress/react-components';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { AdaptToLayoutTemplate } from '../../api';
import { ApiKeyContext } from '../../App';
import { getPhotoSceneUrl, postAdaptToLayoutDocumentUrl } from '../../api';
import adaptToLayoutSourceDocuments from '../../data/adaptToLayout/adaptToLayout_source_documents.json';
import adaptToLayoutSurfaceUrls from '../../data/adaptToLayout/adaptToLayout_surface_urls.json';
import adaptToLayoutWraps from '../../data/adaptToLayout/adaptToLayoutTemplates.json';
import adaptToLayoutTemplateUrls from '../../data/adaptToLayout/adaptToLayout_template_urls.json';
import {
    CardBuilder,
    Control,
    CustomSelector,
    InputJsonSelector,
    Page,
    PreviewRow,
    PreviewRows,
    PreviewSection,
    SurfaceSelector,
} from '../../Components';
import type { SelectorOption } from '../../Components/common/types';
import { getUrl, isUrl, toJsonString } from '../../tools';

const LAYOUT_OPTIONS = [
    { label: 'Image Left, Text Right', shortLabel: 'Img L / Txt R', value: 'image-left-text-right' },
    { label: 'Image Right, Text Left', shortLabel: 'Img R / Txt L', value: 'image-right-text-left' },
    { label: 'Image Top, Text Bottom', shortLabel: 'Img T / Txt B', value: 'image-top-text-bottom' },
    { label: 'Image Bottom, Text Top', shortLabel: 'Img B / Txt T', value: 'image-bottom-text-top' },
    { label: 'Image Only - Center', shortLabel: 'Img Center', value: 'image-only-center' },
    { label: 'Image Only - Top', shortLabel: 'Img Top', value: 'image-only-top' },
    { label: 'Image Only - Bottom', shortLabel: 'Img Bottom', value: 'image-only-bottom' },
    { label: 'Image Only - Left', shortLabel: 'Img Left', value: 'image-only-left' },
    { label: 'Image Only - Right', shortLabel: 'Img Right', value: 'image-only-right' },
    { label: 'Text Only - Center', shortLabel: 'Txt Center', value: 'text-only-center' },
    { label: 'Text Only - Top', shortLabel: 'Txt Top', value: 'text-only-top' },
    { label: 'Text Only - Bottom', shortLabel: 'Txt Bottom', value: 'text-only-bottom' },
    { label: 'Text Only - Left', shortLabel: 'Txt Left', value: 'text-only-left' },
    { label: 'Text Only - Right', shortLabel: 'Txt Right', value: 'text-only-right' },
];

const DEFAULT_LAYOUT = 'image-left-text-right';

const AdaptToLayoutPage = () => {
    const apiKey = useContext(ApiKeyContext);

    const [documentUrl, setDocumentUrlState] = useState('');
    const [surfaceSpecificationUrl, setSurfaceSpecificationUrl] = useState('');
    const [layout, setLayout] = useState('');
    const [template, setTemplate] = useState<AdaptToLayoutTemplate | undefined>(undefined);
    const [templateUrl, setTemplateUrl] = useState('');
    const [templateJson, setTemplateJson] = useState<unknown>(undefined);
    const [useTemplateUrl, setUseTemplateUrl] = useState(true);
    const [hasInitializedFromUrl, setHasInitializedFromUrl] = useState(false);
    const [minimumFontSize, setMinimumFontSize] = useState('');

    const [sourceRow, setSourceRow] = useState(PreviewRow());
    const [resultRow, setResultRow] = useState(PreviewRow());

    const predefinedDocuments = adaptToLayoutSourceDocuments as SelectorOption[];
    const predefinedWraps = adaptToLayoutWraps as any[];
    const predefinedTemplateUrls = adaptToLayoutTemplateUrls as SelectorOption[];

    const setDocumentUrl = (docUrlOrToken: string) => {
        setDocumentUrlState(getUrl(docUrlOrToken) ?? '');
    };

    const hasRequiredInput = documentUrl && surfaceSpecificationUrl && layout;

    const [searchParams, setSearchParams] = useSearchParams();

    // READ from URL on page load (runs once)
    useEffect(() => {
        const urlDocumentUrl = searchParams.get('documentUrl');
        const urlSurfaceUrl = searchParams.get('surfaceSpecificationUrl');
        const urlLayout = searchParams.get('layout');
        const urlTemplateUrl = searchParams.get('templateUrl');
        const urlMinimumFontSize = searchParams.get('minimumFontSize');

        setDocumentUrlState(urlDocumentUrl ?? '');
        setSurfaceSpecificationUrl(urlSurfaceUrl ?? '');
        setLayout(urlLayout ?? DEFAULT_LAYOUT);
        const finalTemplateUrl = urlTemplateUrl ?? '';
        setTemplateUrl(finalTemplateUrl);
        setMinimumFontSize(urlMinimumFontSize ?? '');
        // Set default template
        if (finalTemplateUrl && isUrl(finalTemplateUrl)) {
            setTemplate({ templateUrl: finalTemplateUrl });
        }
        setHasInitializedFromUrl(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // WRITE to URL whenever inputs change
    useEffect(() => {
        if (!hasInitializedFromUrl) {
            return;
        }

        const params: Record<string, string> = {};

        if (documentUrl) params['documentUrl'] = documentUrl;
        if (surfaceSpecificationUrl) params['surfaceSpecificationUrl'] = surfaceSpecificationUrl;
        if (layout) params['layout'] = layout;
        if (templateUrl && useTemplateUrl) params['templateUrl'] = templateUrl;
        if (minimumFontSize) params['minimumFontSize'] = minimumFontSize;

        const nextSearchParams = new URLSearchParams(params);

        if (nextSearchParams.toString() !== searchParams.toString()) {
            setSearchParams(nextSearchParams);
        }
    }, [
        documentUrl,
        surfaceSpecificationUrl,
        layout,
        templateUrl,
        useTemplateUrl,
        minimumFontSize,
        hasInitializedFromUrl,
        searchParams,
        setSearchParams,
    ]);

    const onTemplateUrlChange = useCallback((url: string) => {
        setTemplateUrl(url);
        if (url && isUrl(url)) {
            setTemplate({ templateUrl: url });
        } else if (!url) {
            setTemplate(undefined);
        }
    }, []);

    const onTemplateJsonChange = useCallback((value: unknown) => {
        setTemplateJson(value);
        if (value && typeof value === 'object') {
            setTemplate({ templateJson: value });
        } else {
            setTemplate(undefined);
        }
    }, []);

    // PREVIEW SOURCE DOCUMENT
    useEffect(() => {
        if (!documentUrl) {
            setSourceRow(PreviewRow());
            return;
        }

        if (surfaceSpecificationUrl) {
            const sourceCard = CardBuilder.ofType('documentUrl')
                .withTitle('Source Document')
                .withLoader(async ({ signal }) => {
                    const photoSceneUrl = (await getPhotoSceneUrl(surfaceSpecificationUrl, signal)) ?? '';

                    return {
                        documentUrl: documentUrl,
                        photoSceneUrl,
                        surfaceUrl: surfaceSpecificationUrl,
                    };
                })
                .build();

            setSourceRow(PreviewRow(sourceCard));
        } else {
            const sourceCard = CardBuilder.ofType('documentUrl')
                .withTitle('Source Document')
                .withData({
                    documentUrl: documentUrl,
                })
                .build();

            setSourceRow(PreviewRow(sourceCard));
        }
    }, [documentUrl, surfaceSpecificationUrl]);

    // GENERATE RESULT PREVIEW
    useEffect(() => {
        if (!hasRequiredInput) {
            setResultRow(PreviewRow());
            return;
        }

        const resultCard = CardBuilder.ofType('documentUrl')
            .withTitle(`Adapted Layout: ${layout}`)
            .withLoader(async ({ signal }) => {
                const photoSceneUrl = (await getPhotoSceneUrl(surfaceSpecificationUrl, signal)) ?? '';
                const adaptedDocumentUrl = await postAdaptToLayoutDocumentUrl({
                    documentUrl,
                    surfaceSpecificationUrl,
                    template: template,
                    layout,
                    apiKey,
                    signal,
                    minimumFontSize: minimumFontSize || undefined,
                });

                return {
                    documentUrl: adaptedDocumentUrl,
                    photoSceneUrl,
                    surfaceUrl: surfaceSpecificationUrl,
                };
            })
            .build();

        setResultRow(PreviewRow(resultCard));
    }, [hasRequiredInput, documentUrl, surfaceSpecificationUrl, template, layout, apiKey, minimumFontSize]);

    return (
        <Page>
            <Page.Title>Adapt to Layout</Page.Title>

            <Page.Controls>
                <Control>
                    <Control.Title>Document URL</Control.Title>
                    <CustomSelector
                        selectorLabel="Document"
                        textfieldLabel="Document URL"
                        data={predefinedDocuments}
                        value={documentUrl}
                        setValue={setDocumentUrl}
                        validator={isUrl}
                        alwaysShowInput={true}
                    />
                </Control>

                <Control>
                    <Control.Title>Surface Input</Control.Title>
                    <SurfaceSelector
                        surfaceSpecificationUrl={surfaceSpecificationUrl}
                        setSurfaceSpecificationUrl={setSurfaceSpecificationUrl}
                        presetSurfaceUrlsFromPage={adaptToLayoutSurfaceUrls}
                    />
                </Control>

                <Control>
                    <Control.Title>Minimum Font Size</Control.Title>
                    <TextField
                        label="Minimum Font Size (e.g. 10pt)"
                        value={minimumFontSize}
                        onChange={(e) => setMinimumFontSize(e.target.value)}
                    />
                </Control>

                <Control>
                    <Control.Title>Template/Wrap Input</Control.Title>
                    <TabCard
                        selectedIndex={useTemplateUrl ? 0 : 1}
                        tabs={[
                            {
                                name: 'From URL',
                                block: (
                                    <Control>
                                        <Control.Title>templateUrl</Control.Title>
                                        <CustomSelector
                                            selectorLabel="Template"
                                            textfieldLabel="Template URL"
                                            data={predefinedTemplateUrls}
                                            value={templateUrl}
                                            setValue={(value) => {
                                                onTemplateUrlChange(value);
                                            }}
                                            validator={isUrl}
                                            alwaysShowInput={true}
                                        />
                                    </Control>
                                ),
                            },
                            {
                                name: 'From JSON',
                                block: (
                                    <Control>
                                        <Control.Title>templateJson</Control.Title>
                                        <InputJsonSelector
                                            key={templateJson ? toJsonString(templateJson) : 'empty-template-json'}
                                            label="Template JSON"
                                            initialValue={templateJson}
                                            onChange={onTemplateJsonChange}
                                            predefinedValues={predefinedWraps}
                                            rows={15}
                                        />
                                    </Control>
                                ),
                            },
                        ]}
                        onSelect={(_, selectedKey: number) => {
                            setUseTemplateUrl(selectedKey === 0);
                            if (selectedKey === 0) {
                                // Switched to URL, clear JSON
                                setTemplateJson(undefined);
                                if (templateUrl && isUrl(templateUrl)) {
                                    setTemplate({ templateUrl });
                                }
                            } else {
                                // Switched to JSON, clear URL
                                setTemplateUrl('');
                                if (templateJson && typeof templateJson === 'object') {
                                    setTemplate({ templateJson });
                                }
                            }
                        }}
                    />
                </Control>

                <Control>
                    <Control.Title>Layout Inputs</Control.Title>
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                            gap: '0.75rem',
                        }}
                    >
                        {LAYOUT_OPTIONS.map((option) => {
                            const isSelected = option.value === layout;

                            return (
                                <Button
                                    key={option.value}
                                    variant={isSelected ? 'primary' : 'secondary'}
                                    onClick={() => setLayout(option.value)}
                                    style={{ minHeight: '3rem', whiteSpace: 'normal' }}
                                >
                                    {option.shortLabel}
                                </Button>
                            );
                        })}
                    </div>
                </Control>
            </Page.Controls>

            <Page.Content>
                <PreviewSection showIntro={false} rows={PreviewRows(sourceRow, resultRow)} />
            </Page.Content>
        </Page>
    );
};
export default AdaptToLayoutPage;
